import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, MessageCircle, Send, X } from "lucide-react";
import { api } from "../lib/api";
import { streamPost } from "../lib/stream";

const KEY = "ss_help_session";
const HIDE = ["/admin", "/promo"];
const SUGGEST = ["What sizes do you do?", "How much is delivery?", "How do squad links work?", "Can I buy a gift voucher?"];
const sessionId = () => {
  let s = localStorage.getItem(KEY);
  if (!s) { s = `help-${crypto.randomUUID()}`; localStorage.setItem(KEY, s); }
  return s;
};

const Bubble = ({ m }) => (
  <div className={`max-w-[85%] whitespace-pre-wrap px-3 py-2 text-sm leading-snug ${m.role === "user" ? "ml-auto bg-slate-900 text-white" : "bg-slate-100 text-slate-900"}`} data-testid={`help-msg-${m.role}`}>{m.text || <Loader2 className="h-4 w-4 animate-spin" />}</div>
);

export const HelpChat = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => { api.get("/help/status").then((r) => setEnabled(r.data.enabled)).catch(() => {}); }, []);
  useEffect(() => { if (open && !msgs.length) api.get(`/help/chat/${sessionId()}`).then((r) => setMsgs(r.data.messages)).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  if (!enabled || HIDE.some((p) => pathname.startsWith(p))) return null;

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    const put = (fn) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: fn(m[m.length - 1].text) }]);
    try { await streamPost("/help/chat", { session_id: sessionId(), message }, (t) => put((old) => old + t)); }
    catch (e) { put(() => e.message); } finally { setBusy(false); }
  };
  const lift = pathname.startsWith("/design") ? "bottom-[92px] lg:bottom-5" : "bottom-5";
  return (
    <>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} aria-label="Ask us a question" data-testid="help-chat-open"
          className={`fixed right-4 z-50 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-bold text-lime-300 shadow-xl transition-transform hover:-translate-y-0.5 ${lift}`}>
          <MessageCircle className="h-5 w-5" /> <span className="hidden sm:inline">Questions?</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-x-2 bottom-2 z-50 flex max-h-[80vh] flex-col border-2 border-slate-900 bg-white shadow-2xl sm:inset-x-auto sm:right-5 sm:w-[380px]" data-testid="help-chat-panel" role="dialog" aria-label="Sports Shields help">
          <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
            <div><div className="font-display text-base font-black uppercase">Ask Sports Shields</div><div className="text-[11px] text-slate-300">AI assistant · replies in seconds</div></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="flex h-9 w-9 items-center justify-center bg-white/10" data-testid="help-chat-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="min-h-[200px] flex-1 space-y-2 overflow-y-auto p-3" data-testid="help-chat-messages">
            <Bubble m={{ role: "assistant", text: "Hi! I can help with sizes, prices, delivery, squad links and gift vouchers. What would you like to know?" }} />
            {!msgs.length && <div className="flex flex-wrap gap-1.5 pt-1">{SUGGEST.map((s) => (
              <button key={s} type="button" onClick={() => send(s)} className="border-2 border-slate-200 px-2.5 py-1 text-xs font-bold hover:border-slate-900" data-testid="help-chat-suggestion">{s}</button>))}</div>}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t-2 border-slate-900 p-2">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Type your question…" aria-label="Your question"
              className="min-w-0 flex-1 border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-900" data-testid="help-chat-input" />
            <button type="submit" disabled={busy || !text.trim()} aria-label="Send" className="flex w-11 items-center justify-center bg-lime-400 disabled:opacity-50" data-testid="help-chat-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
          <p className="px-3 pb-2 text-[10px] text-slate-400">AI answers can be wrong – for order problems email sales@businessprint.ie.</p>
        </div>
      )}
    </>
  );
};
